import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { carrierService } from '../../services/carrierService';
import {
  PencilIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const CarrierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carrier, setCarrier] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicles');

  useEffect(() => {
    fetchCarrierData();
  }, [id]);

  useEffect(() => {
    if (carrier) {
      if (activeTab === 'vehicles') {
        fetchVehicles();
      } else if (activeTab === 'account') {
        fetchAccount();
      }
    }
  }, [activeTab, carrier]);

  const fetchCarrierData = async () => {
    setIsLoading(true);
    try {
      const carrierResponse = await carrierService.getCarrierById(id);
      if ( carrierResponse.data ) {
        setCarrier(carrierResponse.data);
      } else {
        throw new Error('Taşıyıcı bilgileri alınamadı');
      }
    } catch (error) {
      toast.error('Taşıyıcı bilgileri yüklenirken bir hata oluştu');
      navigate('/carriers');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const vehiclesResponse = await carrierService.getCarrierVehicles(id, { limit: 10 });
      if ( vehiclesResponse.data) {
        setVehicles(vehiclesResponse.data.rows || []);
      } else {
        setVehicles([]);
      }
    } catch (error) {
      toast.error('Araç bilgileri yüklenirken bir hata oluştu');
      setVehicles([]);
    }
  };

  const fetchAccount = async () => {
    try {
      const accountResponse = await carrierService.getCarrierAccount(id);
      if (accountResponse.data) {
        setAccount(accountResponse.data);
      } else {
        setAccount(null);
      }
    } catch (error) {
      toast.error('Hesap bilgileri yüklenirken bir hata oluştu');
      setAccount(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!carrier) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500">Taşıyıcı bulunamadı.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{carrier.name}</h1>
        <div className="mt-3 flex sm:mt-0 sm:ml-4">
          <Link
            to={`/carriers/${id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PencilIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Düzenle
          </Link>
        </div>
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Taşıyıcı Bilgileri</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Taşıyıcı detayları ve iletişim bilgileri.</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Taşıyıcı adı</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.name}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Taşıyıcı kodu</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.carrier_code}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">E-posta adresi</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.email || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Telefon</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.phone || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">İletişim kişisi</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.contact_person || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Adres</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{carrier.address || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Durum</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  carrier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {carrier.is_active ? 'Aktif' : 'Pasif'}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="mt-6">
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Sekme seçin
          </label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
          >
            <option value="vehicles">Araçlar</option>
            <option value="account">Hesap</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('vehicles')}
                className={`${
                  activeTab === 'vehicles'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <TruckIcon className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === 'vehicles' ? 'text-primary-500' : 'text-gray-400'
                }`} />
                Araçlar
              </button>
              <button
                onClick={() => handleTabChange('account')}
                className={`${
                  activeTab === 'account'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <CurrencyDollarIcon className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === 'account' ? 'text-primary-500' : 'text-gray-400'
                }`} />
                Hesap
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Sekme İçeriği */}
      <div className="mt-6">
        {activeTab === 'vehicles' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Araçlar</h3>
              <Link
                to={`/vehicles/create?carrierId=${id}`}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Yeni Araç Ekle
              </Link>
            </div>
            {vehicles.length === 0 ? (
              <div className="text-center p-6">
                <p className="text-gray-500">Bu taşıyıcıya ait araç bulunamadı.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {vehicles.map((vehicle) => (
                  <li key={vehicle.id}>
                    <Link to={`/vehicles/${vehicle.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-primary-600 truncate">
                              {vehicle.plate_number}
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                vehicle.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {vehicle.is_active ? 'Aktif' : 'Pasif'}
                              </p>
                            </div>
                          </div>
                          <div className="ml-2 flex-shrink-0 flex">
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              {vehicle.vehicleType?.name || 'Araç Tipi Belirtilmemiş'}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              Kapasite: {vehicle.vehicleType?.capacity || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {vehicles.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <Link
                  to={`/vehicles?carrierId=${id}`}
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Tüm araçları görüntüle <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === 'account' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Hesap Bilgileri</h3>
            </div>
            {!account ? (
              <div className="text-center p-6">
                <p className="text-gray-500">Hesap bilgileri yüklenemedi.</p>
              </div>
            ) : (
              <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl className="sm:divide-y sm:divide-gray-200">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Bakiye</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {account.balance} {account.currency}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Son güncelleme</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {new Date(account.last_updated).toLocaleString('tr-TR')}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <Link
                to={`/transactions?entityType=carrier&entityId=${id}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Tüm işlemleri görüntüle <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarrierDetail;
